import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { updateFilter } from 'api/Filter';

import useModal from 'lib/hooks/useModal';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import { useFilters } from 'lib/hooks/queries/filters/useFilters';
import { deepEqual, ia, iaRa, io, objMap } from 'lib/helpers/utility';

import Button from 'components/shared/Button/Button';
import Divider from 'components/shared/Divider/Divider';
import Popover from 'components/shared/Popovers/Popover/Popover';

import { getActiveFilters } from '../lib/helpers';

import SaveFilterModal from './SaveFilterModal';

const FilterActions = ({
  filters,
  initialFilters,
  changeFilters,
  category,
  isEditingFilter,
  disableActions = false,
  items = [],
  type
}) => {
  const { isOpen, openModal, closeModal } = useModal();
  const {
    isOpen: isCreateViewOpen,
    openModal: openCreateViewModal,
    closeModal: closeCreateViewModal
  } = useModal();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const activeFilters = useMemo(
    () => getActiveFilters(filters).filter(([_, filter]) => !filter?.default),
    [filters]
  );

  const isInitialFilter = useMemo(
    () => deepEqual(initialFilters, filters),
    [initialFilters, filters]
  );

  const filterName = searchParams.get('view');

  const { data } = useFilters({
    params: { category },
    dependencies: [category]
  });

  const handleClearFilters = useCallback(() => {
    changeFilters(initialFilters);
    if (isEditingFilter) return;

    setSearchParams((prev) => {
      prev.delete('view');
      return prev;
    });
  }, [initialFilters, changeFilters, setSearchParams, isEditingFilter]);

  const { mutate: updateFilterHandler } = useCustomMutation({
    mutationFn: (params) => updateFilter(navigate, params),
    invalidateQueryKey: ['filters', category],
    successMessage: 'Filter updated',
    errorMessage: "Couldn't update filter. Please try again."
  });

  const handleSaveView = async () => {
    const filter = iaRa(data?.filters).find((filter) => filter.name === filterName);
    await updateFilterHandler({ id: filter.id, name: filter.name, filters });
  };

  const handleCloseModal = () => {
    closeModal();
    closeCreateViewModal();
  };

  const isSaveViewOpen = !!isOpen || !!isCreateViewOpen;
  const wasCreatingViewRef = useRef(false);

  useEffect(() => {
    if (isSaveViewOpen) wasCreatingViewRef.current = !!isCreateViewOpen;
  }, [isSaveViewOpen, isCreateViewOpen]);

  const isCreatingView = isSaveViewOpen ? !!isCreateViewOpen : wasCreatingViewRef.current;

  const isSameFilter = useMemo(() => {
    const appliedFilters = objMap(filters, (k, f) => f?.values);
    const filter = iaRa(data?.filters).find((filter) => filter.name === filterName);
    if (!filter) return false;
    return deepEqual(JSON.parse(filter?.filters), appliedFilters);
  }, [data?.filters, filterName, filters]);

  if (disableActions)
    return (
      (!isInitialFilter || isEditingFilter) && (
        <Button
          variant="link"
          color={isEditingFilter ? 'neutral' : 'danger'}
          shade={isEditingFilter ? 700 : 500}
          size="xs"
          text={isEditingFilter ? 'Cancel' : 'Clear'}
          onClick={handleClearFilters}
        />
      )
    );

  return (
    <>
      {!isInitialFilter && (ia(activeFilters) || io(initialFilters)) && (
        <>
          <Divider direction="vertical" className="mt-1 !h-4 max-h-4" />
          <Button
            variant="link"
            color={isEditingFilter ? 'neutral' : 'danger'}
            shade={isEditingFilter ? 700 : 500}
            size="xs"
            text={isEditingFilter ? 'Cancel' : 'Clear'}
            onClick={handleClearFilters}
          />
          {isEditingFilter && !isSameFilter ? (
            <Popover
              customButtonTrigger={
                <Button.Dropdown
                  variant="tertiary"
                  size="xs"
                  className="whitespace-nowrap"
                  iconRight="chevron-down-linear"
                  iconSize={16}
                  iconShade={700}
                  dropdownClassName="rounded-[4px]"
                  text="Save view"
                  iconColor="neutral"
                />
              }
              position="left"
              options={[
                {
                  label: 'Save to this view',
                  value: 'save-view',
                  icon: 'save-mono',
                  onClick: handleSaveView
                },
                {
                  label: 'Create a new view',
                  value: 'create-new-view',
                  icon: 'add-square-mono',
                  onClick: openCreateViewModal
                }
              ]}
            />
          ) : !isEditingFilter && !isSameFilter ? (
            <Button
              variant="tertiary"
              size="xs"
              className="whitespace-nowrap"
              iconSize={16}
              iconShade={700}
              dropdownClassName="rounded-[4px]"
              text="Save view"
              iconColor="neutral"
              onClick={openModal}
            />
          ) : null}
        </>
      )}
      <SaveFilterModal
        isOpen={isSaveViewOpen}
        onCancel={handleCloseModal}
        filters={filters}
        category={category}
        currentViews={data?.filters}
        currentView={
          isCreatingView ? null : iaRa(data?.filters).find((filter) => filter.name === filterName)
        }
        items={items}
        type={type}
      />
    </>
  );
};

export default FilterActions;
